import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import FileReaderWrapper from "@/commons/utils/FileReaderWrapper";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { getType } from "mime";

/**
 * S3Url API 操作クラス
 */
export default class S3Url {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET S3Url
   */
  public async get(userStore: LoginUser, expireHour: number, url: string): Promise<string> {
    // S3key情報（ファイルのURLからバケット名を消したもの）を作成する
    // 基本的に施設に関わるファイルはバケット名 + /facility-で境界値を特定する
    const sliceTopIndex: number = url.indexOf("/facility-");
    console.log("S3Url.get() sliceTopIndex:", sliceTopIndex);
    let preUrl: string = url.substr(0, sliceTopIndex + 1);
    // 全施設共通の情報の場合（sliceTopIndex==-1）の場合、バケット名を使用して対応する
    if (sliceTopIndex == -1) {
      preUrl = `${RestUrl.S3Url}/${process.env.VUE_APP_IMAGE_BUCKETNAME}/`;
    }
    console.log("S3Url.get() preUrl:", preUrl);

    console.log("S3Url.get() url:", url);
    const imageKey = String(url).replace(preUrl, "");
    console.log("S3Url.get() imageKey:", imageKey);
    const filePath = decodeURIComponent(imageKey);
    console.log("S3Url.get() filePath:", filePath);

    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.PublicS3Url, formatParams);
    const queryParams: any = {
      bucketName: process.env.VUE_APP_IMAGE_BUCKETNAME,
      expireHour: Number(expireHour),
      filePath: filePath
    };

    const response = await this.commonService.executeGet<{ url: string }>(userStore, requestUrl, this.constructor.name, queryParams, HitomeqErrorType.REQUEST_RETRY, 0);

    console.log("S3Url.get() response.data.url:", response.data.url);

    return response.data.url;
  }

  public async getFile(userStore: LoginUser, expireHour: number, url: string, fileName?: string): Promise<File> {
    // blobに以下2つのプロパティを生やせばFileオブジェクトになる
    const blob: any = await this.getBlob(userStore, expireHour, url);
    blob.lastModifiedDate = new Date();

    if (fileName == undefined) {
      fileName = this.getFileName(url);
    }

    // メッセージ送信などでPDF⇒jpeg変換時にファイル名に拡張子が含まれない問題の対応
    // .pdf_1pageのように変換したページ番号が拡張子として渡された場合は末尾に.jpegを付与して画像として扱う
    // eslint-disable-next-line no-useless-escape
    const regex: RegExp = /\.pdf\_(\d+)page$/;
    const matches: RegExpMatchArray | null = fileName.match(regex);
    if (matches) {
      fileName = fileName + ".jpeg";
    }

    let mime: string | null = this.getMimeType(fileName);
    if (mime == null) {
      console.error("S3Url::getS3File() mime is null");
      mime = "";
    }

    const file = new File([blob], fileName, { type: mime });

    return file;
  }

  public async getDataUrl(userStore: LoginUser, expireHour: number, url: string): Promise<string> {
    const blob: any = await this.getBlob(userStore, expireHour, url);
    const dateUrl: string = await FileReaderWrapper.readAsDataURL(blob);

    return dateUrl;
  }

  public async getBlob(userStore: LoginUser, expireHour: number, url: string): Promise<Blob> {
    const fileName: string = this.getFileName(url);
    let mime: string | null = this.getMimeType(fileName);
    if (mime == null) {
      console.error("S3Url::getS3File() mime is null");
      mime = "";
    }
    const s3Url: string = await this.get(userStore, expireHour, url);
    const response: any = await window.axios.get(s3Url, {
      responseType: "blob",
      headers: { "Content-Type": mime }
    });

    return response.data;
  }

  /**
   * ### URLからファイル名を取得する
   *
   * @param url URL
   */
  public getFileName(url: string): string {
    const _file: Array<string> = url.split("/");
    return _file[_file.length - 1];
  }

  /**
   * ### MimeTypeを取得する
   *
   * @param fileName ファイル名
   */
  public getMimeType(fileName: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileName[0] == ".") {
      contentType = getType(`file${fileName}`);
    } else {
      contentType = getType(`${fileName}`);
    }
    return contentType;
  }
}
